import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import image from '../assets/mountvoqlogo.jpg';

function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Show logout confirmation dialog
  const confirmLogout = () => {
    setShowLogoutConfirm(true);
  };
  
  // Handle actual logout
  const handleLogout = () => {
    // Remove token from localStorage
    localStorage.removeItem('authToken');
    // Redirect to login page
    navigate('/login');
    // Close the confirmation dialog
    setShowLogoutConfirm(false);
  };
  
  // Cancel logout
  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const navigation = [
    { 
      name: 'Dashboard', 
      href: '/', 
      icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
      submenu: [
        { name: 'Overview', href: '/' },
        { name: 'Platform Analytics', href: '/dashboard/analytics' }
      ]
    },
    { 
      name: 'User Management', 
      href: '/users', 
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      submenu: [
        { name: 'Salon Owners', href: '/users/salon-owners' },
        { name: 'Job Seekers', href: '/users/job-seekers' }
      ]
    },
    { 
      name: 'Job Listings', 
      href: '/jobs', 
      icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      submenu: [
        { name: 'Active', href: '/jobs/active' },
      ]
    },
    { 
      name: 'Training', 
      href: '/training', 
      icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
      submenu: [
        { name: 'Modules', href: '/training/modules' },
        { name: 'Inquiries', href: '/training/inquiries' }
      ]
    },
    { 
      name: 'News & Blog', 
      href: '/content', 
      icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
      submenu: [
        { name: 'Whats Trending', href: '/content/blogs' },
        { name: 'Products', href: '/training/products' },
        { name: 'Consultations', href: '/content/workshops' }
      ]
    },
    { 
      name: 'Settings', 
      href: '/settings', 
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
      submenu: [
        { name: 'System', href: '/settings/system' },
        //{ name: 'Profile', href: '/settings/profile' }
      ]
    },
  ];

  // State to track which menu items are expanded
  const [expandedItems, setExpandedItems] = useState({});

  // Initialize expanded items based on current location
  useEffect(() => {
    const currentPath = location.pathname;
    const newExpandedItems = {};
    
    navigation.forEach(item => {
      if (item.submenu && item.submenu.some(sub => currentPath === sub.href)) {
        newExpandedItems[item.name] = true;
      }
    });
    
    setExpandedItems(newExpandedItems);
  }, [location.pathname]);

  // Toggle submenu visibility
  const toggleSubmenu = (name) => {
    setExpandedItems(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Render navigation items with submenu support
  const renderNavItems = (items, isMobile = false) => {
    return items.map((item) => {
      const hasSubmenu = item.submenu && item.submenu.length > 0;
      const isExpanded = expandedItems[item.name];
      const isActive = location.pathname === item.href || 
                      (hasSubmenu && item.submenu.some(sub => location.pathname === sub.href));
      
      return (
        <div key={item.name} className="mb-1">
          {/* Main menu item */}
          <div 
            className={`${
              isActive 
                ? 'bg-white bg-opacity-20 text-white border-l-4 border-orange-400' 
                : 'text-white hover:bg-white hover:bg-opacity-10 hover:text-white hover:border-l-4 hover:border-orange-400'
            } group flex items-center px-4 py-3 text-sm font-medium rounded-r-lg cursor-pointer transition-all duration-200 ease-in-out`}
            onClick={() => hasSubmenu ? toggleSubmenu(item.name) : isMobile && setIsOpen(false)}
          >
            <svg
              className={`${
                isActive ? 'text-orange-400' : 'text-white group-hover:text-orange-400'
              } mr-3 h-5 w-5 flex-shrink-0 transition-colors duration-200`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
            </svg>
            
            {hasSubmenu ? (
              <div className="flex justify-between items-center w-full">
                <span className="font-medium">{item.name}</span>
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            ) : (
              <Link to={item.href} className="w-full font-medium" onClick={() => isMobile && setIsOpen(false)}>
                {item.name}
              </Link>
            )}
          </div>
          
          {/* Submenu items with animation */}
          {hasSubmenu && (
            <div 
              className={`ml-8 mt-1 space-y-1 overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {item.submenu.map(subItem => (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  className={`${
                    location.pathname === subItem.href 
                      ? 'bg-white bg-opacity-10 text-orange-300 border-l-2 border-orange-400' 
                      : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-5 hover:text-orange-300 hover:border-l-2 hover:border-orange-400'
                  } block px-3 py-2 rounded-r-md text-xs font-medium transition-all duration-200 ease-in-out`}
                  onClick={() => isMobile && setIsOpen(false)}
                >
                  {subItem.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      {/* Mobile sidebar overlay with blur effect */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm md:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* Mobile sidebar with slide animation */}
      <div className={`fixed inset-y-0 left-0 flex z-50 w-72 transition-transform duration-300 ease-in-out transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:hidden`}>
        <div className="flex flex-col w-full bg-gradient-to-br from-gray-900 via-purple-900 to-orange-700 shadow-2xl">
          <div className="flex justify-end items-center h-16 px-6 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm">
            <button 
              className="text-white hover:text-orange-300 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="">
                <div className="bg-black bg-opacity-20 backdrop-filter backdrop-blur-sm rounded-xl p-4 shadow-inner border border-white border-opacity-10">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-9 w-10 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-lg shadow-md">
                        MP
                      </div>
                    </div>
                    <div className="ml-3">
                    <Link to="/" className="text-xs text-gray-300">Salonsyncr Professionals</Link>
                    </div>
                  </div>
                </div>
              </div>
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto scrollbar-thin scrollbar-thumb-orange-500 scrollbar-track-transparent">
            <nav className="mt-5 flex-1 px-4 space-y-1">
              {renderNavItems(navigation, true)}
            </nav>
          </div>
          
          {/* Logout Button for Mobile - Moved to absolute bottom with glass effect */}
          <div className="px-4 py-4 border-t border-white border-opacity-10 bg-black bg-opacity-20 backdrop-filter backdrop-blur-sm">
            <button
              onClick={confirmLogout}
              className="group flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 w-full shadow-md transition-all duration-200 ease-in-out transform hover:scale-[1.02]"
            >
              <svg
                className="mr-3 h-5 w-5 text-white group-hover:text-gray-100"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar with glass effect */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-gradient-to-br from-gray-900 via-purple-900 to-orange-700 shadow-xl relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -ml-32 -mb-32"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500 opacity-10 rounded-full blur-3xl"></div>
            
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto scrollbar-thin scrollbar-thumb-orange-500 scrollbar-track-transparent relative z-10">
              {/* <div className="flex items-center justify-center flex-shrink-0 px-4 py-0 mb-5">
                <img 
                  src={image} 
                  alt="Company Logo" 
                  style={{ 
                    height: '120px', 
                    width: 'auto', 
                    maxWidth: '100%',
                    borderRadius: '15px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
                  }} 
                  className="object-contain hover:shadow-2xl transition-shadow duration-300" 
                />
              </div> */}
              
              {/* User profile section */}
              <div className="px-4 mb-6">
                <div className="bg-black bg-opacity-20 backdrop-filter backdrop-blur-sm rounded-xl p-4 shadow-inner border border-white border-opacity-10">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-lg shadow-md">
                        MP
                      </div>
                    </div>
                    <div className="ml-3">
                      <Link to="/" className="text-xs text-gray-300">Salonsyncr Professionals</Link>
                    </div>
                  </div>
                </div>
              </div>
              
              
              <nav className="flex-1 px-3 space-y-1">
                {renderNavItems(navigation)}
                
                {/* Logout Button for Desktop */}
                <div className="pt-6">
                  <button
                    onClick={confirmLogout}
                    className="group flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 w-full shadow-md transition-all duration-200 ease-in-out transform hover:scale-[1.02]"
                  >
                    <svg
                      className="mr-3 h-5 w-5 text-white group-hover:text-gray-100"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
      
      {/* Logout Confirmation Modal with glass morphism */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="relative bg-white bg-opacity-90 backdrop-filter backdrop-blur-md rounded-xl overflow-hidden shadow-2xl transform transition-all sm:max-w-lg sm:w-full">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-red-600"></div>
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                  <svg className="h-10 w-10 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-xl leading-6 font-bold text-gray-900" id="modal-title">
                  Confirm Logout
                </h3>
                <div className="mt-3">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to log out? You will need to sign in again to access your account.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex space-x-3 justify-end">
              <button
                type="button"
                className="px-4 py-2 bg-white border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                onClick={cancelLogout}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 border border-transparent rounded-md font-medium text-white hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
































